import React, { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import Home from '@components/Home'
import PrivateRoute from '@components/PrivateRoute'
import ScrollToTopButton from '@components/ScrollToTopButton'
import useSignInHandler from '@components/SignInHandler'
import SubscriptionRoute from '@components/SubscriptionRoute'
import CommonLayout from '@components/layouts/CommonLayout'
import { Audit } from '@modules/audit'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { usePendoIntegration } from '@modules/auth/hooks/usePendoIntegration'
import { userSubscriptionLevel } from '@modules/auth/types'
import { ChatProvider } from '@modules/chat/useChatContext'
import HubPage from '@modules/hub'
import Profile from '@modules/profile'
import Search from '@modules/search'
import SettingsPage from '@modules/settings'
import ShortlistPage from '@modules/shortlist'
import AlertsPage from '@modules/updates'
import useIsTestEnv from '@shared/hooks/useIsTestEnv'
import { usePersistStore } from '@shared/hooks/usePersistStore'

import { logPageView } from './ga'
import useToggleLocalization from './i18n/useToggleLocalization'

const App = () => {
  const { isAuthenticated, isFetching = true } = useAuthContext()
  const { previousUrlPathname, clearUrlData } = usePersistStore()
  const isTestEnv = useIsTestEnv()

  useToggleLocalization()

  const location = useLocation()

  useSignInHandler()
  usePendoIntegration()

  useEffect(() => {
    logPageView()
  }, [location])

  useEffect(() => {
    if (isAuthenticated && previousUrlPathname === location.pathname) {
      clearUrlData()
    }
  }, [isAuthenticated, location.pathname, previousUrlPathname, clearUrlData])

  return !isFetching ? (
    <Routes>
      <Route
        path="/plus"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <SubscriptionRoute
              accessibleLevels={[
                userSubscriptionLevel.SUBSCRIBED_USER,
                userSubscriptionLevel.ECOMMERCE,
                userSubscriptionLevel.VENDOR,
              ]}
            >
              <ChatProvider>
                <Home />
                <ScrollToTopButton />
              </ChatProvider>
            </SubscriptionRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/plus/hub"
        element={
          <PrivateRoute isAuthenticated={isAuthenticated}>
            <ChatProvider>
              <HubPage />
              <ScrollToTopButton />
            </ChatProvider>
          </PrivateRoute>
        }
      />
      {isTestEnv ? (
        <Route
          path="/plus/audit"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <Audit />
            </PrivateRoute>
          }
        />
      ) : null}
      <Route element={<CommonLayout />}>
        <Route
          path="/plus/search"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <SubscriptionRoute
                accessibleLevels={[
                  userSubscriptionLevel.SUBSCRIBED_USER,
                  userSubscriptionLevel.ECOMMERCE,
                  userSubscriptionLevel.VENDOR,
                ]}
              >
                <ChatProvider>
                  <Search />
                  <ScrollToTopButton />
                </ChatProvider>
              </SubscriptionRoute>
            </PrivateRoute>
          }
        />
      </Route>

      <Route element={<CommonLayout />}>
        <Route
          path="/plus/custom-list"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <SubscriptionRoute
                accessibleLevels={[
                  userSubscriptionLevel.SUBSCRIBED_USER,
                  userSubscriptionLevel.ECOMMERCE,
                  userSubscriptionLevel.VENDOR,
                ]}
              >
                <ChatProvider>
                  <ShortlistPage />
                  <ScrollToTopButton />
                </ChatProvider>
              </SubscriptionRoute>
            </PrivateRoute>
          }
        />
      </Route>

      <Route element={<CommonLayout />}>
        <Route
          path="/plus/alerts/:tab"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <SubscriptionRoute
                accessibleLevels={[
                  userSubscriptionLevel.SUBSCRIBED_USER,
                  // userSubscriptionLevel.ECOMMERCE,
                  userSubscriptionLevel.VENDOR,
                ]}
              >
                <ChatProvider>
                  <AlertsPage />
                </ChatProvider>
              </SubscriptionRoute>
            </PrivateRoute>
          }
        />
      </Route>

      <Route element={<CommonLayout />}>
        <Route
          path="/plus/settings"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <ChatProvider>
                <SettingsPage />
                <ScrollToTopButton />
              </ChatProvider>
            </PrivateRoute>
          }
        />
      </Route>

      <Route element={<CommonLayout />}>
        <Route
          path="/plus/profile/:profileId/*"
          element={
            <PrivateRoute isAuthenticated={isAuthenticated}>
              <ChatProvider>
                <Profile />
                <ScrollToTopButton />
              </ChatProvider>
            </PrivateRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/plus/hub" replace />} />
    </Routes>
  ) : null
}

export default App
